import { useQuery } from "@apollo/client";
import AssignmentIndOutlined from "@mui/icons-material/AssignmentIndOutlined";
import AssignmentLateOutlined from "@mui/icons-material/AssignmentLateOutlined";
import Autorenew from "@mui/icons-material/Autorenew";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import ListAltOutlined from "@mui/icons-material/ListAltOutlined";
import PendingOutlined from "@mui/icons-material/PendingOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import RateReviewOutlined from "@mui/icons-material/RateReviewOutlined";
import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { gql } from "generated-graphql";

import { AssignedByStaff } from "./AssignedByStaff";

// All @mui/icons-material icons share this component type; reuse one to type the
// tile icon field without importing the type from the (bundle-heavy) icons barrel.
type IconComponent = typeof RateReviewOutlined;

const MANIFEST_REVIEW_SUMMARY_QUERY = gql(`
  query ManifestReviewSummary {
    staffManifestReviewSummary {
      total
      assignedToMe
      unassigned
      blocked
      docReview
      notProcessed
      needsRefresh
      missingEManifest
      openDiscrepancies
      epaCorrections
      overdue45
      overdue60
      completed
      assignedCounts {
        user {
          id
          email
          person {
            first
            last
          }
        }
        count
      }
    }
  }
`);

/** Upsert/remove keyed omnisearch terms; null clears that key. */
export type ApplyFilter = (updates: Record<string, string[] | null>) => void;

/** Toggle a single keyed value on/off; `exclusive` keys set/clear the whole key. */
export type ToggleFilter = (key: string, value: string, opts?: { exclusive?: boolean }) => void;

interface CountBoxProps {
  label: string;
  value: number | undefined;
  loading: boolean;
  /** Leading icon — doubles as a legend for the grid's Staff Actions icons. */
  Icon: IconComponent;
  /** When set, the tile is clickable and applies an omnisearch filter; otherwise display-only. */
  onClick?: () => void;
  /** True when this tile's filter is currently applied — renders the "on" state. */
  active?: boolean;
}

/**
 * A scoreboard tile: a muted leading icon + label on the left (label wraps), count on
 * the right. Equal width so every tile is the same size; light-grey resting fill. The
 * icon is a darker grey so it reads as a quiet legend mark. Clickable tiles turn light
 * blue on hover and STAY highlighted (blue fill + border) while their filter is active,
 * so they read as toggles; display-only tiles stay static.
 */
function CountBox({ label, value, loading, Icon, onClick, active }: CountBoxProps) {
  return (
    <Box
      role={onClick ? "button" : undefined}
      aria-pressed={onClick ? Boolean(active) : undefined}
      aria-label={onClick ? `Filter by ${label}` : undefined}
      onClick={onClick}
      sx={{
        width: "100%",
        minHeight: 48,
        borderRadius: 1,
        px: 1.5,
        py: 1,
        border: 1,
        borderColor: active ? "primary.main" : "transparent",
        bgcolor: active ? (theme) => alpha(theme.palette.primary.main, 0.16) : "grey.100",
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        transition: "background-color 0.15s ease",
        "&:hover": onClick
          ? {
              bgcolor: (theme) => alpha(theme.palette.primary.main, active ? 0.22 : 0.12),
            }
          : undefined,
      }}
    >
      {/* flex:1 + minWidth:0 lets the label take the free space and wrap within it, so a
          long single-word label (e.g. "Unassigned") can never overflow into the fixed
          value; overflowWrap breaks an over-long word rather than spilling past the tile. */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
        <Icon
          sx={{
            fontSize: 18,
            color: active ? "primary.main" : "grey.500",
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          color={active ? "primary.main" : "text.secondary"}
          sx={{ lineHeight: 1.25, minWidth: 0, overflowWrap: "anywhere" }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" component="span" sx={{ fontWeight: 500, flexShrink: 0 }}>
        {loading ? <Skeleton width={24} /> : (value ?? 0)}
      </Typography>
    </Box>
  );
}

export function GroupLabel({ children }: { children: string }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
      {children}
    </Typography>
  );
}

export function ManifestReviewSummary({
  onApplyFilter,
  onToggle,
  onClearAll,
  activeTerms,
  completedActive,
  onSelectCompleted,
}: {
  onApplyFilter: ApplyFilter;
  onToggle: ToggleFilter;
  /** Clear every filter (the "Total Manifests" reset). */
  onClearAll: () => void;
  activeTerms: Record<string, string[]>;
  /** True when the Completed tab is active — renders the Completed tile as "on". */
  completedActive: boolean;
  /** Switch to the Completed tab (the Completed tile is navigation, not a filter toggle). */
  onSelectCompleted: () => void;
}) {
  const { data, loading } = useQuery(MANIFEST_REVIEW_SUMMARY_QUERY);
  const summary = data?.staffManifestReviewSummary;
  const assignedCounts = summary?.assignedCounts ?? [];

  const isActive = (key: string, value: string) => (activeTerms[key] ?? []).includes(value);
  // A toggle tile: clicking adds the keyed term and stays highlighted; clicking again
  // removes just that term. staffAction tiles share one multi-value key (so several
  // combine, OR-matched); assignee / missingEManifest are exclusive single-value keys.
  const toggleTile = (
    key: string,
    value: string,
    opts?: { exclusive?: boolean }
  ): Pick<CountBoxProps, "active" | "onClick"> => ({
    active: isActive(key, value),
    onClick: () => onToggle(key, value, opts),
  });

  const groups: { label: string; tiles: CountBoxProps[] }[] = [
    {
      label: "Assignment",
      tiles: [
        {
          label: "Total Manifests",
          value: summary?.total,
          loading,
          Icon: ListAltOutlined,
          // A reset — clears every filter (keyed terms + free text).
          onClick: onClearAll,
        },
        {
          label: "Unassigned",
          value: summary?.unassigned,
          loading,
          Icon: PersonOutline,
          ...toggleTile("assignee", "unassigned", { exclusive: true }),
        },
        {
          label: "Assigned to Me",
          value: summary?.assignedToMe,
          loading,
          Icon: AssignmentIndOutlined,
          ...toggleTile("assignee", "me", { exclusive: true }),
        },
        {
          // Manifests taken out of the actionable queue with a reason. Toggles the
          // status:BLOCKED filter so the (bottom-sorted) blocked rows can be listed alone.
          label: "Blocked",
          value: summary?.blocked,
          loading,
          Icon: BlockOutlined,
          ...toggleTile("status", "BLOCKED", { exclusive: true }),
        },
        {
          // Global cumulative count of finished manifests (last 18 months). Clicking it
          // navigates to the Completed tab rather than toggling an omnisearch term.
          label: "Completed",
          value: summary?.completed,
          loading,
          Icon: CheckCircleOutline,
          active: completedActive,
          onClick: onSelectCompleted,
        },
      ],
    },
    {
      label: "Document Review",
      tiles: [
        {
          label: "Needs review",
          value: summary?.docReview,
          loading,
          // Same icon as the grid's Doc Review staff-action mark (legend).
          Icon: RateReviewOutlined,
          ...toggleTile("staffAction", "docReview"),
        },
        {
          label: "Not processed",
          value: summary?.notProcessed,
          loading,
          Icon: PendingOutlined,
          // The same list as the Document Review "Not Processed" state/filter.
          ...toggleTile("documentReviewState", "NOT_PROCESSED"),
        },
        {
          label: "New version",
          value: summary?.needsRefresh,
          loading,
          Icon: Autorenew,
          // Reviewed source with a newer unprocessed version (documentReviewState).
          ...toggleTile("documentReviewState", "NEEDS_REFRESH"),
        },
      ],
    },
    {
      label: "Discrepancies",
      tiles: [
        {
          label: "Field Discrepancies",
          value: summary?.openDiscrepancies,
          loading,
          // Same icon as the grid's Discrepancies staff-action mark (legend).
          Icon: ErrorOutline,
          ...toggleTile("staffAction", "discrepancyReview"),
        },
        {
          label: "EPA Corrections",
          value: summary?.epaCorrections,
          loading,
          // Same icon as the grid's EPA Corrections staff-action mark (legend).
          Icon: GavelOutlined,
          ...toggleTile("staffAction", "epaCorrections"),
        },
      ],
    },
    {
      label: "e-Manifest Compliance",
      tiles: [
        {
          label: "Missing e-Manifest",
          value: summary?.missingEManifest,
          loading,
          Icon: DescriptionOutlined,
          ...toggleTile("missingEManifest", "true", { exclusive: true }),
        },
        {
          label: "Needs vendor contact",
          value: summary?.overdue45,
          loading,
          // Same icon as the grid's Vendor Contact staff-action mark (legend).
          Icon: PhoneOutlined,
          ...toggleTile("staffAction", "vendorContact"),
        },
        {
          label: "Needs exception report",
          value: summary?.overdue60,
          loading,
          // Same icon as the grid's Exception Report staff-action mark (legend).
          Icon: AssignmentLateOutlined,
          ...toggleTile("staffAction", "exceptionReport"),
        },
      ],
    },
  ];

  return (
    // Summary (grouped tiles) at 3/4, Assignees as its own paper at 1/4 — the wider
    // split gives the icon + label rows (esp. e-Manifest Compliance) room to breathe.
    <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="stretch">
      <Paper variant="outlined" sx={{ p: 3, flex: 3, minWidth: 0 }}>
        {/* Each section is a column (horizontal across the panel); its tiles stack
            vertically. Columns are equal flex width so all tiles end up the same
            size; they wrap to a column layout on narrow screens. */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="flex-start">
          {groups.map((group) => (
            <Box key={group.label} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              <GroupLabel>{group.label}</GroupLabel>
              <Stack spacing={1}>
                {group.tiles.map((tile) => (
                  <CountBox key={tile.label} {...tile} />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 3, flex: 1, minWidth: 0 }}>
        <AssignedByStaff assignedCounts={assignedCounts} onApplyFilter={onApplyFilter} />
      </Paper>
    </Stack>
  );
}
